import { navigate } from 'gatsby';
import { find, map, propEq } from 'ramda';
import React, { FC, useMemo } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';
import TextLink from '@ui/atoms/TextLink';

import ContactCard from '@ui/molecules/ContactCard';
import DashedLinkButton from '@ui/molecules/DashedLinkButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { doesNotExist, exists } from '@utils/general';

import relationships from '@data/relationships';

import { IPageProps } from '@folx-types/flow-steps';
import { IEmergencyContact } from '@folx-types/member';

import theme from '@theme/FolxTheme';

interface IEmergencyContactsProps extends IPageProps {
	loading: boolean;
	contacts?: IEmergencyContact[];
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

const EmergencyContacts: FC<IEmergencyContactsProps> = ({ contacts, loading = true }) => {
	const getContactCards = useMemo(() => {
		return map((contact) => {
			const relationship = find<{ value: string; label: string }>(
				propEq('value', contact?.relationship)
			)(relationships);

			const phoneNumber = exists(formatPhoneNumber(contact.phoneNumber))
				? formatPhoneNumber(contact.phoneNumber)
				: contact.phoneNumber;

			return (
				<ContactCard
					key={contact.id}
					id={contact.id}
					name={`${contact.firstName} ${contact.lastName}`}
					relationship={relationship?.label ?? ''}
					phone={phoneNumber}
					email={contact?.email}
					handleClick={() => {
						navigate('./edit', { state: { contact: contact } });
					}}
				/>
			);
		}, contacts ?? []);
	}, [contacts]);

	return (
		<>
			<SpacedColumn gap="md" marginBottom="xxl">
				<HeadingText>Emergency contacts</HeadingText>
				<LoadingWrapper
					loading={loading}
					skeleton={<Skeleton repeated={2} structure="column" height="xxxxl" gap="sm" fullWidth />}>
					<FullWidthColumn gap="sm">
						{getContactCards}
						{(doesNotExist(contacts) || contacts?.length < 2) && (
							<DashedLinkButton href="./edit" label="add a contact" />
						)}
					</FullWidthColumn>
				</LoadingWrapper>
			</SpacedColumn>
			<TextLink label="Back to Account" href=".." />
		</>
	);
};

export default EmergencyContacts;
